import React, { useMemo } from 'react';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import StackedTopEntityChart from 'src/components/BeaconV2Components/TopEntityChartV2/StackedTopEntityChart';
import { useMainTimePeriod, useMainTimePeriodShortDisplayName } from 'src/utils/Hooks';
import { useStacklineTheme } from '@stackline/ui';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import LegendWithMultipleElement from 'src/components/BeaconV2Components/Legend/LegendWithMultipleElement';
import { useAggregationBuilderWithMetrics } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy';
import useGenericAdvancedSearch from 'src/utils/Hooks/useGenericAdvancedSearch';
import { AggregationValue } from 'src/components/BeaconV2Components/utils/AdvancedSearchParser/types';
import { useBaseMetricRequestBuilder } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { saveAs } from 'file-saver';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';

export interface StackedChartDataSet {
  name: string;
  data: { y: number; color: string }[];
  color: string;
}

interface SentimentTopicData {
  positiveSentimentData: AggregationValue[];
  neutralSentimentData: AggregationValue[];
  negativeSentimentData: AggregationValue[];
}

const useSentimentByTopicData = (
  metric: string
): {
  data: SentimentTopicData | null;
  isLoading: boolean;
} => {
  const { startWeek, endWeek } = useMainTimePeriod();

  const requestBuilder = useBaseMetricRequestBuilder({
    requestId: 'sentimentByTopic',
    indexName: 'review-metrics',
    startWeekId: startWeek,
    endWeekId: endWeek,
    fields: []
  });

  if (metric === 'cumulative') {
    requestBuilder.clearConditionRangeFilters();
    requestBuilder.addConditionRangeFilter('weekId', undefined, endWeek);
  }

  requestBuilder.clearAggregations();
  const aggregationBuilder = useAggregationBuilderWithMetrics({
    indexName: 'review-metrics',
    fields: [{ name: 'aspectSentiment' }],
    groupByFieldName: 'aspect.keyword'
  });

  aggregationBuilder
    .setGroupByFieldName('aspect.keyword')
    .setSortByAggregationField('Aspect Sentiment', 'aspectSentiment', 'value_count', false);

  const queryList = [
    ['negative', -1],
    ['neutral', 0],
    ['positive', 1]
  ].reduce((requestGroup, [sentimentType, sentimentId]) => {
    const req = requestBuilder.clone();
    req.setId(`sentimentByTopic-${sentimentType}`);

    const agg = aggregationBuilder
      .clone()
      .removeConditionTermFilter('retailerId')
      .addConditionTermFilter('aspectSentiment', [`${sentimentId}`]);
    if (metric === 'cumulative') {
      agg.clearConditionRangeFilters();
    }

    req.addAggregation(agg.build());

    return [...requestGroup, req.build()];
  }, []);

  const { data: response, isLoading } = useGenericAdvancedSearch({
    requestId: 'sentimentByTopic-stackedColumn',
    requestBody: queryList,
    queryKeys: [queryList]
  });

  const data = useMemo(() => {
    if (response) {
      try {
        const [negativeResponse, neutralResponse, positiveResponse] = response?.data ?? [];
        const positiveSentimentData = positiveResponse?.aggregations?.['by_aspect.keyword'];
        const neutralSentimentData = neutralResponse?.aggregations?.['by_aspect.keyword'];
        const negativeSentimentData = negativeResponse?.aggregations?.['by_aspect.keyword'];

        return {
          positiveSentimentData,
          neutralSentimentData,
          negativeSentimentData
        };
      } catch (error) {
        console.error('Failed to parse sentiment data in CumulativeSentimentByTopicColumnChart', error);
        return null;
      }
    }
    return null;
  }, [response]);

  return { data, isLoading };
};

// Creates the dataset for the stacked column chart
const useSentimentStackedChartDataset = (data: SentimentTopicData | null): StackedChartDataSet[] => {
  const theme = useStacklineTheme();
  if (data) {
    const { positiveSentimentData, neutralSentimentData, negativeSentimentData } = data;

    // Gather all unique topics across all sentiment types because some topics may only appear in one sentiment type.
    const allUniqueTopics = new Set([
      ...positiveSentimentData.map((datum) => datum.additionalMetaData.name),
      ...neutralSentimentData.map((datum) => datum.additionalMetaData.name),
      ...negativeSentimentData.map((datum) => datum.additionalMetaData.name)
    ]);

    // Map the bucketed data to more usable format
    const positiveData = positiveSentimentData.map((datum) => ({
      y: datum.value,
      name: datum.fieldId
    }));
    const neutralData = neutralSentimentData.map((datum) => ({
      y: datum.value,
      name: datum.fieldId
    }));
    const negativeData = negativeSentimentData.map((datum) => ({
      y: datum.value,
      name: datum.fieldId
    }));

    // Create an array of all unique topics
    const uniqueTopics = Array.from(allUniqueTopics);

    // Determine the total volume of reviews for each topic.
    // This will be used to sort the unique topics by review volume.
    const reviewVolumeByTopic = uniqueTopics.reduce((volumeByTopic, topic) => {
      // Find the review volume for each sentiment type
      const positiveValue = positiveData.find((datum) => datum.name === topic)?.y ?? 0;
      const neutralValue = neutralData.find((datum) => datum.name === topic)?.y ?? 0;
      const negativeValue = negativeData.find((datum) => datum.name === topic)?.y ?? 0;
      const totalVolume = positiveValue + neutralValue + negativeValue;

      volumeByTopic[topic] = totalVolume;

      return volumeByTopic;
    }, {} as { [key: string]: number });

    // Sort the topics by review volume
    const sortedTopicVolumeTuples = Object.entries(reviewVolumeByTopic).sort((a, b) => b[1] - a[1]);

    // Map the sorted topics to the stacked chart dataset
    const topicMetrics = sortedTopicVolumeTuples.reduce(
      (acc, [topic]) => {
        // Each topic we iterate over will be in order of descending review volume
        // We can use the topic to find the review volume by sentiment type
        const positiveReviewVolume = positiveData.find((datum) => datum.name === topic)?.y ?? 0;
        const neutralReviewVolume = neutralData.find((datum) => datum.name === topic)?.y ?? 0;
        const negativeReviewVolume = negativeData.find((datum) => datum.name === topic)?.y ?? 0;

        // Create the data objects for each sentiment type
        const positiveDatum = { name: topic, y: positiveReviewVolume, color: theme.colors.accentPine };
        const neutralDatum = { name: topic, y: neutralReviewVolume, color: theme.colors.accentGold };
        const negativeDatum = { name: topic, y: negativeReviewVolume, color: theme.colors.accentMagenta };
        acc.sortedPositiveData.push(positiveDatum);
        acc.sortedNeutralData.push(neutralDatum);
        acc.sortedNegativeData.push(negativeDatum);

        return acc;
      },
      { sortedPositiveData: [], sortedNeutralData: [], sortedNegativeData: [] }
    );

    return [
      { name: 'Negative', data: topicMetrics.sortedNegativeData, color: theme.colors.accentMagenta },
      { name: 'Neutral', data: topicMetrics.sortedNeutralData, color: theme.colors.accentGold },
      { name: 'Positive', data: topicMetrics.sortedPositiveData, color: theme.colors.accentPine }
    ];
  } else {
    return [
      { name: 'Negative', data: [], color: theme.colors.accentMagenta },
      { name: 'Neutral', data: [], color: theme.colors.accentGold },
      { name: 'Positive', data: [], color: theme.colors.accentPine }
    ];
  }
};

const SentimentByTopicColumnChart = () => {
  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();
  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();

  const { data, isLoading } = useSentimentByTopicData('weekly');

  const chartSeries = useSentimentStackedChartDataset(data);

  return (
    <BeaconChartWithLegend
      convertSeriesToDelimitedData={async () => {
        const fileTitle = 'csv_data';
        const headers = ['Topic', 'Positive', 'Neutral', 'Negative'];
        try {
          const positiveData = chartSeries.find((series) => series.name === 'Positive')?.data;
          const neutralData = chartSeries.find((series) => series.name === 'Neutral')?.data;
          const negativeData = chartSeries.find((series) => series.name === 'Negative')?.data;

          const csvData = positiveData.map((positiveDatum, index) => {
            const topic = positiveDatum?.name || '';
            const positiveValue = positiveDatum.y;
            const neutralValue = neutralData[index]?.y ?? 0;
            const negativeValue = negativeData[index]?.y ?? 0;

            return [topic, positiveValue, neutralValue, negativeValue];
          });
          const csvDataString = [headers, ...csvData].map((row) => row.join(',')).join('\n');

          const blob = new Blob([csvDataString], { type: 'text/csv;charset=utf-8' });
          saveAs(blob, `${fileTitle}.csv`);
        } catch (error) {
          console.error('Failed to save chart as delimited data in CumulativeSentimentByTopicColumnChart', error);
        }
      }}
      loading={isLoading}
      loadingComponent={<SplineChartLoading width={900} />}
      subtitle={
        <BeaconSubtitleContainer>
          <Text variant="body1">
            {`The chart displays the sentiment grouped by topic for ${entityPhrase} `}
            {taxonomyText}
            {` from reviews written during the ${mainTimePeriodShortDisplayName} period.`}
            <SubtitleFilters />
          </Text>
        </BeaconSubtitleContainer>
      }
      subtitleSX={{ marginTop: '8px' }}
      includeSummaryInfo={false}
      customLegends={
        <LegendWithMultipleElement
          multipleLegendsProps={[
            { displayName: 'Positive', barColor: 'accentPine' },
            { displayName: 'Neutral', barColor: 'accentGold' },
            { displayName: 'Negative', barColor: 'accentMagenta' }
          ]}
        />
      }
      title="Sentiment by Topic"
    >
      <StackedTopEntityChart
        chartSeries={[
          {
            type: 'column',
            data: chartSeries,
            metricType: METRICTYPE.VOLUME
          }
        ]}
        tooltipCategoryTitle="REVIEW VOLUME"
      />
    </BeaconChartWithLegend>
  );
};

export default SentimentByTopicColumnChart;
